import { Container, EmptyState, Table } from "@fluidattacks/design";
import type { Row as tanRow } from "@tanstack/react-table";
import { capitalize } from "lodash";
import isEmpty from "lodash/isEmpty";
import React, { StrictMode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { getColumns } from "./utils";

import { SectionHeader } from "components/section-header";
import { useAudit } from "hooks/use-audit";
import { useAuthz } from "hooks/use-authz";
import { useTable } from "hooks/use-table";
import type {
  IOrganizationPortfoliosProps,
  IPortfolios,
  IPortfoliosTable,
} from "pages/organization/portfolios/types";
import { openUrl } from "utils/resource-helpers";

const OrganizationPortfolios: React.FC<IOrganizationPortfoliosProps> = ({
  organizationId,
  portfolios,
}): JSX.Element => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = useAuthz();
  const canGetGroups = can("integrates_api_resolvers_tag_groups_resolve");
  const [search, setSearch] = useState("");

  const { addAuditEvent } = useAudit();
  useEffect((): void => {
    addAuditEvent("Organization.Portfolios", organizationId);
  }, [addAuditEvent, organizationId]);

  const formatPortfolioDescription = (
    groups: { name: string }[] = [],
  ): string => {
    const MAX_NUMBER_OF_GROUPS = 6;
    const mainDescription = groups
      .map((group): string => capitalize(group.name))
      .slice(0, MAX_NUMBER_OF_GROUPS)
      .join(", ");
    const remaining = groups.length - MAX_NUMBER_OF_GROUPS;
    const remainingDescription =
      remaining > 0
        ? t("organization.tabs.portfolios.remainingDescription", { remaining })
        : "";

    return mainDescription + remainingDescription;
  };

  const goToDocs = useCallback((): void => {
    openUrl(
      "https://help.fluidattacks.com/portal/en/kb/articles/sort-groups-into-portfolios",
    );
  }, []);

  const formatPortfolioTableData = (
    portfoliosList: IPortfolios[],
  ): IPortfoliosTable[] =>
    portfoliosList.map(
      (portfolio): IPortfoliosTable => ({
        groups: formatPortfolioDescription(portfolio.groups),
        id: portfolio.name,
        nGroups: portfolio.groups?.length ?? 0,
        portfolio: portfolio.name,
      }),
    );

  const goToPortfolio = useCallback(
    (portfolioName: string): void => {
      navigate(`${pathname}/${portfolioName.toLowerCase()}/analytics`);
    },
    [navigate, pathname],
  );

  const handleRowClick = useCallback(
    (rowInfo: tanRow<IPortfoliosTable>): void => {
      goToPortfolio(rowInfo.getValue("portfolio"));
    },
    [goToPortfolio],
  );

  // Table config
  const handleSearch = useCallback((value: string): void => {
    setSearch(value);
  }, []);

  const formattedData = formatPortfolioTableData(portfolios).filter(
    (portfolio): boolean =>
      portfolio.portfolio.toLowerCase().includes(search.toLowerCase()),
  );

  const { table } = useTable({
    columns: getColumns(canGetGroups),
    data: formattedData,
    id: "portfolios-table",
    options: {
      actionsOptions: {
        onSearch: handleSearch,
        search,
        searchPlaceholder: t("organization.portfolios.searchPlaceholder"),
      },
      filtersMode: "client-side",
      paginationMode: "client-side",
      rowOptions: {
        onClick: handleRowClick,
      },
    },
    total: formattedData.length,
  });

  const sectionHeader = (
    <SectionHeader header={t("organization.tabs.portfolios.title")} />
  );

  if (isEmpty(portfolios)) {
    return (
      <React.Fragment>
        {sectionHeader}
        <div className={"flex flex-column items-center"}>
          <EmptyState
            cancelButton={{
              onClick: goToDocs,
              text: t("app.learnMore"),
            }}
            description={t("organization.tabs.portfolios.empty.description")}
            imageSrc={"integrates/empty/portfoliosIcon"}
            title={t("organization.tabs.portfolios.empty.title")}
          />
        </div>
      </React.Fragment>
    );
  }

  return (
    <StrictMode>
      {sectionHeader}
      <Container px={1.25} py={1.25}>
        {isEmpty(portfolios) ? undefined : (
          <Table table={table}>
            <Table.Options>
              <Table.Controls>
                <Container id={"actions-container"} />
              </Table.Controls>
            </Table.Options>
            <Table.Content />
            <Table.Footer />
          </Table>
        )}
      </Container>
    </StrictMode>
  );
};

export { OrganizationPortfolios };
